import React from 'react';
import { FaFileLines, FaBolt, FaDownload, FaLock, FaPenRuler, FaClock } from 'react-icons/fa6';
import { usePageData } from '../../contexts/DataContext';

// Icon mapping
const iconComponents = {
  FaFileLines: FaFileLines,
  FaBolt: FaBolt,
  FaDownload: FaDownload,
  FaLock: FaLock,
  FaPenRuler: FaPenRuler,
  FaClock: FaClock,
  MdAttachMoney: () => <span>💰</span>,
  MdPayment: () => <span>💳</span>,
  FaCalendarAlt: () => <span>📅</span>,
  FaFileInvoice: () => <span>📄</span>,
  FaUserTie: () => <span>👔</span>,
  FaChartLine: () => <span>📈</span>,
  FaShieldAlt: () => <span>🛡️</span>,
  FaHeart: () => <span>❤️</span>,
  FaGem: () => <span>💎</span>,
};

export const getIconComponent = (iconName) => {
  return iconComponents[iconName] || (() => <span>📋</span>);
};

// Skeleton Component
const FeaturesSkeleton = () => (
  <section className="features">
    <style>
      {`
        @keyframes skeleton-loading {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
      `}
    </style>

    <div className="container">
      {/* Title skeleton */}
      <div
        style={{
          height: "32px",
          width: "50%",
          marginBottom: "12px",
          marginLeft: "auto",
          marginRight: "auto",
          borderRadius: "4px",
          background:
            "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
          backgroundSize: "400% 100%",
          animation: "skeleton-loading 1.4s ease infinite",
        }}
      />

      {/* Subtitle skeleton */}
      <div
        style={{
          height: "18px",
          width: "70%",
          marginBottom: "24px",
          marginLeft: "auto",
          marginRight: "auto",
          borderRadius: "4px",
          background:
            "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
          backgroundSize: "400% 100%",
          animation: "skeleton-loading 1.4s ease infinite",
        }}
      />

      <div className="divider"></div>

      <div className="feature-grid">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div className="feature-card" key={i}>
            {/* Icon skeleton */}
            <div
              style={{
                height: "40px",
                width: "40px",
                marginBottom: "12px",
                borderRadius: "50%",
                background:
                  "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
                backgroundSize: "400% 100%",
                animation: "skeleton-loading 1.4s ease infinite",
              }}
            />

            {/* Heading skeleton */}
            <div
              style={{
                height: "20px",
                width: "80%",
                marginBottom: "8px",
                borderRadius: "4px",
                background:
                  "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
                backgroundSize: "400% 100%",
                animation: "skeleton-loading 1.4s ease infinite",
              }}
            />

            {/* Text skeleton */}
            <div
              style={{
                height: "14px",
                width: "100%",
                borderRadius: "4px",
                background:
                  "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
                backgroundSize: "400% 100%",
                animation: "skeleton-loading 1.4s ease infinite",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FeaturesSection = () => {
  const { data: homePage, isLoading } = usePageData('homePage');

  // Show skeleton when loading and no data
  if (isLoading && !homePage) {
    return <FeaturesSkeleton />;
  }

  // Use actual data or fallback (only when not loading)
  const features = homePage?.features || [];
  const hasFeatures = features.length > 0;

  return (
    <section className="features">
      <div className="container">
        <h2 className="section-title">Everything You Need, Nothing You Don't</h2>
        <p className="section-subtitle">Simple, powerful tools to create professional resumes, cover letters, and slips that get you noticed. Customize, download, and manage your documents easily — all in one place.</p>
        <div className="divider"></div>

        <div className="feature-grid">
          {hasFeatures ? (
            features.map((feature, index) => {
              const IconComponent = getIconComponent(feature.icon);
              
              return (
                <div className="feature-card" key={index}>
                  <div className="feature-icon">
                    <i className={feature.icon}></i>
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              );
            })
          ) : (
            // Minimal fallback when no features (should rarely happen)
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
              <p>Loading features...</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;